import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { FaAngleDown, FaToggleOn, FaToggleOff } from "react-icons/fa";
import { MdOutlinePhone } from "react-icons/md";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LoaderSpiner from "../../Components/LoaderSpiner";
import styles from "./CallBook.module.css";
import { fetchEventSource } from '@microsoft/fetch-event-source';

// Utility function to access nested fields
const getNestedField = (obj, path) => {
  return path.split(".").reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
};

// Utility function to safely render a value (handles objects, arrays, and edge cases)
const renderCellValue = (value) => {
  if (value === null || value === undefined) return "N/A";
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(", ") : "N/A";
  }
  if (typeof value === "object") {
    if (value.text && value.hyperlink) {
      return <a href={value.hyperlink}>{value.text}</a>;
    }
    if (Object.keys(value).length > 0) {
      return Object.entries(value)
        .map(([key, val]) => `${key}: ${val}`)
        .join(", ");
    }
    return "N/A";
  }
  // Map backend statuses to UI-friendly strings
  const statusMap = {
    initiated: "Initiated",
    "in-progress": (
      <span>
        LIVE <span style={{ color: "green", fontSize: "10px" }}>●</span>
      </span>
    ),
    completed: "Completed",
    joined: "Completed",
  };
  return statusMap[value] || value.toString();
};

// ZeroSecondConnect toggle component
const ZeroSecondConnectToggle = ({ campaign, onToggle }) => {
  const [isToggling, setIsToggling] = useState(false);
  const [zeroSecondActive, setZeroSecondActive] = useState(campaign.zeroSecondConnect || false);
  const [metaCampaignExists, setMetaCampaignExists] = useState(!!campaign.metaCampaignId);
  const isWebBased = campaign.campaignType?.toLowerCase() === "web based";


  const handleToggle = async () => {
    if (isToggling) return;

    setIsToggling(true);

    try {
      const newStatus = !zeroSecondActive;
      const payload = {
        campaignId: campaign._id,
        zeroSecondConnect: newStatus
      };

      const token = localStorage.getItem("token");
      const response = await axios.put(
        "https://prod.toxboxai.com/api/campaign/zero-second-connect/toggle",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setZeroSecondActive(newStatus);
        toast.success(
          newStatus ?
            "Zero-Second Connect started successfully!" :
            "Zero-Second Connect stopped successfully!"
        );

        // Update local campaign data
        onToggle(campaign._id, newStatus);

        // If enabling and metaCampaignExists, show additional info
        if (newStatus && metaCampaignExists) {
          toast.info("Zero-Second Connect will now automatically call new leads from your Meta campaign!", {
            autoClose: 5000
          });
        }
      } else {
        toast.error(response.data.message || "Failed to toggle Zero-Second Connect");
      }
    } catch (error) {
      console.error("Toggle error:", error);
      const errorMsg = error.response?.data?.message || error.message || "Failed to toggle Zero-Second Connect";
      toast.error(errorMsg);

      // Show specific error messages
      if (error.response?.status === 400) {
        toast.error("Invalid campaign or permissions. Please check your access.");
      } else if (error.response?.status === 401) {
        toast.error("Authentication failed. Please log in again.");
      } else if (error.response?.status === 403) {
        toast.error("You don't have permission to enable Zero-Second Connect for this campaign.");
      } else if (error.response?.status === 404) {
        toast.error("Campaign not found. Please refresh the page.");
      }
    } finally {
      setIsToggling(false);
    }
  };

  // Only show toggle if metaCampaignId exists (Meta campaign)
  if ((!metaCampaignExists) && (!isWebBased)) {
    return null;
  }

  const toggleButtonStyle = {
    backgroundColor: zeroSecondActive ? '#28a745' : '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 12px',
    marginRight: '8px',
    cursor: isToggling ? 'not-allowed' : 'pointer',
    opacity: isToggling ? 0.6 : 1,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    boxShadow: zeroSecondActive ? '0 2px 4px rgba(40, 167, 69, 0.3)' : '0 2px 4px rgba(108, 117, 127, 0.3)'
  };

  const iconStyle = {
    fontSize: '16px'
  };

  const buttonText = zeroSecondActive ? 'Stop Auto-Calls' : 'Start Auto-Calls';

  return (
    <button
      onClick={handleToggle}
      disabled={isToggling}
      style={toggleButtonStyle}
      title={
        zeroSecondActive
          ? "Click to stop automatic zero-second calls for Meta leads"
          : "Click to start automatic zero-second calls for new Meta leads"
      }
    >
      {isToggling ? (
        <span style={{ fontSize: '12px' }}>Toggling...</span>
      ) : (
        <>
          {zeroSecondActive ? <FaToggleOn style={iconStyle} /> : <FaToggleOff style={iconStyle} />}
          {buttonText}
        </>
      )}
    </button>
  );
};

const CallBook = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedCaller, setSelectedCaller] = useState([]);
  const [showCampaignDropdown, setShowCampaignDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [callData, setCallData] = useState({});
  const [successfulCalls, setSuccessfulCalls] = useState(() => {
    const saved = localStorage.getItem("successfulCalls");
    return saved ? JSON.parse(saved) : [];
  });
  const [callResults, setCallResults] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [webhookMapping, setWebhookMapping] = useState({});
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all"); // Added filterStatus state
  const token = localStorage.getItem("token");
  const userID = localStorage.getItem("userId");
  const campaignDropdownRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5; // adjust as needed

  const sseControllerRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("successfulCalls", JSON.stringify(successfulCalls));
  }, [successfulCalls]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        if (!token || !userID) {
          throw new Error("Missing token or user ID. Please log in.");
        }
        const campaignResponse = await axios.get(
          "https://prod.toxboxai.com/api/campaign/assigned-campaigns",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const fetchedCampaigns = campaignResponse.data.data?.campaigns || [];
        console.log("Fetched campaigns:", fetchedCampaigns);
        setCampaigns(fetchedCampaigns.map(c => ({ ...c, sheetData: [] }))); // Set empty sheetData
        if (fetchedCampaigns.length > 0 && !selectedCampaign) {
          setSelectedCampaign({ ...fetchedCampaigns[0], sheetData: [] });
        }
        setError(null);
      } catch (err) {
        console.error("Error fetching campaigns:", err);
        setError(err.message || "Failed to fetch campaigns.");
        setCampaigns([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [token, userID]); // Removed selectedCampaign to prevent loop

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        campaignDropdownRef.current &&
        !campaignDropdownRef.current.contains(event.target)
      ) {
        setShowCampaignDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const processCampaignData = () => {
      if (!selectedCampaign || !selectedCampaign.sheetData) {
        setCallData({});
        return;
      }
      try {
        const responseData = selectedCampaign.sheetData;
        console.log("Sheet data:", responseData);
        const callDataObj = responseData.reduce((acc, user, index) => {
          try {
            const callObj = createCallObj(user, index);
            acc[user.other?.Id || user._id || index] = { user, callObj };
            return acc;
          } catch (error) {
            console.error(`Error processing user ${index}:`, error);
            return acc;
          }
        }, {});
        console.log("Processed call data:", callDataObj);
        setCallData(callDataObj);
      } catch (error) {
        console.error("Error processing campaign data:", error);
        setCallData({});
      }
    };
    processCampaignData();
  }, [selectedCampaign, webhookMapping]);

  useEffect(() => {
    if (error) {
      toast.error(error, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  }, [error]);

  // SSE setup for selected campaign data
  // SSE – CORRECTLY HANDLES YOUR BACKEND FORMAT
  // SSE – PRIORITIZES statusUpdate OVER initialData/campaignUpdate
  useEffect(() => {
    if (!selectedCampaign?._id || !token) return;

    // Abort previous connection
    if (sseControllerRef.current) {
      sseControllerRef.current.abort();
    }
    const controller = new AbortController();
    sseControllerRef.current = controller;

    // Track last status update time to prioritize it
    const lastStatusUpdateRef = { current: null };

    fetchEventSource(
      `https://prod.toxboxai.com/api/campaign/assigned-campaigns/${selectedCampaign._id}/sse`,
      {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,

        onopen: async (response) => {
          if (response.ok) {
            toast.success("Live connection established!");
            console.log("SSE Connected – waiting for data...");
          } else {
            toast.error("SSE connection failed");
          }
        },

        onmessage: (event) => {
          // Ignore keepalive pings
          if (event.data.startsWith(":")) return;

          try {
            const parsed = JSON.parse(event.data);
            console.log("SSE Raw Message:", parsed);

            // PRIORITY 1: statusUpdate event (highest priority)
            if (parsed.event === "statusUpdate" && parsed.leads) {
              console.log("✅ Status update received - overriding all data");
              lastStatusUpdateRef.current = Date.now();

              setSelectedCampaign(prev => ({
                ...prev,
                sheetData: parsed.leads,
              }));

              const summary = parsed.statusSummary || {};
              const total = parsed.totalLeads || 0;
              toast.info(`Status updated: ${total} leads (${Object.entries(summary).map(([k, v]) => `${k}: ${v}`).join(', ')})`);
              return; // IGNORE other events after statusUpdate
            }

            // PRIORITY 2: Check if statusUpdate was recent (within 5 seconds)
            const now = Date.now();
            if (lastStatusUpdateRef.current && (now - lastStatusUpdateRef.current < 5000)) {
              console.log("⏭️ Ignoring event - statusUpdate was recent");
              return; // Skip processing other events
            }

            // PRIORITY 3: initialData (only if no recent statusUpdate)
            if (parsed.event === "initialData" && parsed.campaign?.sheetData) {
              console.log("Initial data received");
              setSelectedCampaign(prev => ({
                ...prev,
                sheetData: parsed.campaign.sheetData,
                ...parsed.campaign,
              }));
              toast.success(`${parsed.campaign.sheetData.length} leads loaded initially!`);
              return;
            }

            // PRIORITY 4: campaignUpdate/new leads (only if no recent statusUpdate)
            if (parsed.type === "newLeadsAdded" && parsed.newLeads?.length > 0) {
              console.log("New leads added");
              setSelectedCampaign(prev => ({
                ...prev,
                sheetData: [...(prev.sheetData || []), ...parsed.newLeads],
              }));
              toast.info(`${parsed.newLeads.length} new leads added!`);
              return;
            }

            // Fallback: direct sheetData
            if (parsed.sheetData) {
              console.log("Direct sheetData update");
              setSelectedCampaign(prev => ({ ...prev, sheetData: parsed.sheetData }));
              toast.success(`Table updated with ${parsed.sheetData.length} leads`);
            }

          } catch (err) {
            console.error("Failed to parse SSE message:", err, event.data);
          }
        },

        onerror: (err) => {
          console.error("SSE Error:", err);
          toast.error("Connection lost – reconnecting in 3s...");
          return 3000; // retry delay
        },

        onclose: () => {
          console.log("SSE Closed");
        },
      }
    );

    return () => controller.abort();
  }, [selectedCampaign?._id, token]);

  // Update campaigns when toggle state changes
  const handleZeroSecondToggle = (campaignId, newStatus) => {
    setCampaigns(prevCampaigns =>
      prevCampaigns.map(campaign =>
        campaign._id === campaignId
          ? { ...campaign, zeroSecondConnect: newStatus }
          : campaign
      )
    );

    // Update selected campaign if it's the one that changed
    if (selectedCampaign?._id === campaignId) {
      setSelectedCampaign(prev => prev ? { ...prev, zeroSecondConnect: newStatus } : prev);
    }
  };

  const createCallObj = (item, index) => {
    const userFields = {
      name: item.name,
      email: item.email,
      phoneNumber: item.phoneNumber,
      callStatus: item.callStatus,
      ...item.other,
    };

    const mappedPhoneNumber = webhookMapping.number
      ? getNestedField(item, webhookMapping.number) || item.phoneNumber
      : item.phoneNumber;

    return {
      toNumber: mappedPhoneNumber ? `+91${mappedPhoneNumber}` : "",
      callNumberId: item._id || "",
      customerName: item.name || "",
      customerDetails: {
        email: item.email || "",
        phoneNumber: item.phoneNumber || "",
        callStatus: item.callStatus || "",
        ...item.other,
      },
      id: item.other?.Id || index,
    };
  };

  const handleCampaignSelect = (campaign) => {
    setSelectedCampaign({ ...campaign, sheetData: [] }); // Reset sheetData; SSE will populate
    setSelectedCaller([]);
    setFilterStatus("all");
    console.log("Selected campaign:", campaign);
    setShowCampaignDropdown(false);
  };

  const filteredData =
    selectedCampaign?.sheetData?.filter((item) => {
      const searchText = searchQuery.toLowerCase();
      const phoneNumber = webhookMapping.number
        ? getNestedField(item, webhookMapping.number) || item.phoneNumber
        : item.phoneNumber;
      const callResult = callResults[phoneNumber];
      const statusMatch =
        item.callStatus === "Not initiated" ||
        item.callStatus === "Initiated" ||
        item.callStatus === "Live" ||
        item.callStatus === "initiated" ||
        item.callStatus === "in-progress" ||
        item.callStatus === "completed" ||
        item.callStatus === "joined";
      let filterMatch = true;
      if (filterStatus === "successful") {
        filterMatch =
          callResult?.success === true ||
          successfulCalls.some(
            (call) => call.id === (item.other?.Id || item._id)
          );
      } else if (filterStatus === "not_attempted") {
        filterMatch =
          !callResult &&
          !successfulCalls.some(
            (call) => call.id === (item.other?.Id || item._id)
          );
      } else if (filterStatus === "missed") {
        filterMatch = callResult?.success === false;
      }
      const searchMatch = Object.values({ ...item, ...item.other }).some(
        (value) => {
          if (value === null || value === undefined) return false;
          return renderCellValue(value)
            .toString()
            .toLowerCase()
            .includes(searchText);
        }
      );
      return statusMatch && filterMatch && searchMatch;
    }) || [];
  const assignments =
    activeTab === "successful" ? successfulCalls : filteredData;
  const totalPages = Math.ceil(assignments.length / itemsPerPage);

  const handleTabClick = (tab) => setActiveTab(tab);

  const handleCheckboxChange = (id) => {
    setSelectedCaller((prev) =>
      prev.includes(id) ? prev.filter((cId) => cId !== id) : [...prev, id]
    );
    console.log("Selected caller IDs:", selectedCaller);
  };

  const handleSelectAll = () => {
    if (selectedCaller.length === filteredData?.length) {
      setSelectedCaller([]);
    } else {
      setSelectedCaller(
        filteredData?.map((item) => item.other?.Id || item._id || item.id)
      );
    }
  };

  const initiateCall = async (calls) => {
    // Set status to "initiated" immediately from frontend
    setCallData((prev) => {
      const updatedCallData = { ...prev };
      calls.forEach((call) => {
        if (updatedCallData[call.id]) {
          updatedCallData[call.id].user.callStatus = "initiated";
        }
      });
      return updatedCallData;
    });

    // Fallback: Transition to "in-progress" after 5 seconds if no event received
    const inProgressTimeout = setTimeout(() => {
      setCallData((prev) => {
        const updatedCallData = { ...prev };
        calls.forEach((call) => {
          if (
            updatedCallData[call.id] &&
            updatedCallData[call.id].user.callStatus === "initiated"
          ) {
            updatedCallData[call.id].user.callStatus = "in-progress";
          }
        });
        console.log(
          "Fallback: Set to in-progress after timeout",
          updatedCallData
        );
        return updatedCallData;
      });
    }, 5000);

    try {
      const payload = {
        campaignId: selectedCampaign?._id || "",
        transferNumber: "+918459645531",
        assignedAgentId: selectedCampaign?.assignedAgent || "",
        calls: calls,
      };
      console.log("Call payload:", JSON.stringify(payload, null, 2));
      const endpoint = `https://prod.toxboxai.com/api/call-plivo/make-call?token=${token}`;
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          errorData.message || `HTTP error! Status: ${response.status}`;
        throw new Error(errorMessage);
      }

      const newResults = {};
      const newSuccessfulCalls = Array.isArray(calls) ? calls : [calls];
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedData = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        accumulatedData += decoder.decode(value, { stream: true });
        try {
          const events = accumulatedData
            .split("\n\n")
            .filter((str) => str.trim());
          accumulatedData = "";
          for (const eventStr of events) {
            if (!eventStr) continue;
            try {
              const lines = eventStr.split("\n");
              const eventLine = lines.find((line) => line.startsWith("event:"));
              const dataLine = lines.find((line) => line.startsWith("data:"));
              if (!eventLine || !dataLine) continue;
              const event = eventLine.replace("event:", "").trim();
              const data = JSON.parse(dataLine.replace("data:", "").trim());
              console.log("Streamed event:", event, data);

              if (event === "status" && data.callId && data.status) {
                console.log(
                  "Processing status event for callId:",
                  data.callId,
                  "Status:",
                  data.status
                );
                // Update callStatus in callData
                setCallData((prev) => {
                  const updatedCallData = { ...prev };
                  Object.values(updatedCallData).forEach((entry) => {
                    if (entry.callObj.callNumberId === data.callId) {
                      entry.user.callStatus = data.status;
                      console.log(
                        "Updated callStatus for callId:",
                        data.callId,
                        "to:",
                        data.status
                      );
                    }
                  });
                  return updatedCallData;
                });
                // Clear timeout if "in-progress" or later status received
                if (
                  data.status === "in-progress" ||
                  data.status === "completed" ||
                  data.status === "joined"
                ) {
                  clearTimeout(inProgressTimeout);
                }
              }

              if (data.successes?.length > 0) {
                // Update callStatus to "joined" for successful calls
                setCallData((prev) => {
                  const updatedCallData = { ...prev };
                  newSuccessfulCalls.forEach((call) => {
                    if (updatedCallData[call.id]) {
                      updatedCallData[call.id].user.callStatus = "joined";
                    }
                  });
                  return updatedCallData;
                });

                setSuccessfulCalls((prev) => [
                  ...prev,
                  ...newSuccessfulCalls.map((call) => ({
                    id: call.id,
                    ...call,
                    timestamp: new Date().toISOString(),
                    apiId: data.successes.find((s) => s.requestUuid)?.apiId,
                    customerDetails: {
                      ...call.customerDetails,
                      callStatus: "joined",
                    },
                  })),
                ]);

                setCallData((prev) => {
                  const updatedCallData = { ...prev };
                  newSuccessfulCalls.forEach((call) => {
                    delete updatedCallData[call.id];
                  });
                  return updatedCallData;
                });

                setSelectedCaller((prev) =>
                  prev.filter(
                    (id) => !newSuccessfulCalls.some((call) => call.id === id)
                  )
                );

                newSuccessfulCalls.forEach((call) => {
                  newResults[call.toNumber] = {
                    success: true,
                    apiId: data.successes.find((s) => s.requestUuid)?.apiId,
                    timestamp: new Date().toISOString(),
                  };
                });

                clearTimeout(inProgressTimeout);
              }

              if (data.failures?.length > 0) {
                // Reset callStatus to "Not initiated" for failed calls
                setCallData((prev) => {
                  const updatedCallData = { ...prev };
                  newSuccessfulCalls.forEach((call) => {
                    if (updatedCallData[call.id]) {
                      updatedCallData[call.id].user.callStatus =
                        "Not initiated";
                    }
                  });
                  return updatedCallData;
                });
                newSuccessfulCalls.forEach((call) => {
                  newResults[call.toNumber] = {
                    success: false,
                    timestamp: new Date().toISOString(),
                  };
                });
                clearTimeout(inProgressTimeout);
              }
            } catch (jsonError) {
              console.warn("Incomplete JSON chunk, accumulating:", eventStr);
              accumulatedData += eventStr + "\n\n";
            }
          }
        } catch (error) {
          console.error("Error processing stream chunk:", error);
        }
      }
      setCallResults((prev) => ({ ...prev, ...newResults }));
      console.log("Call results:", newResults);
      return newResults;
    } catch (error) {
      console.error("Error initiating call:", error);
      let userFriendlyMessage = "Failed to initiate call. Please try again.";

      // Customize error messages based on the error type
      if (error.message.includes("401")) {
        userFriendlyMessage = "Authentication failed. Please log in again.";
      } else if (error.message.includes("400")) {
        userFriendlyMessage =
          "Invalid call data. Please check your selections.";
      } else if (error.message.includes("network")) {
        userFriendlyMessage =
          "Network error. Please check your internet connection.";
      } else if (error.message) {
        userFriendlyMessage = `Failed to initiate call contact to admin`;
      }

      setError(userFriendlyMessage);
      // Reset callStatus to "Not initiated" on error
      setCallData((prev) => {
        const updatedCallData = { ...prev };
        calls.forEach((call) => {
          if (updatedCallData[call.id]) {
            updatedCallData[call.id].user.callStatus = "Not initiated";
          }
        });
        return updatedCallData;
      });
      clearTimeout(inProgressTimeout);
      return null;
    }
  };

  const handleIndividualCall = async (item) => {
    if (!selectedCampaign) {
      alert("Please select a campaign.");
      return;
    }
    const callObj = createCallObj(item.user, item.id || 0);
    console.log("callObj:", callObj);
    await initiateCall([callObj]);
  };

  const handleDynamicCall = async () => {
    if (!selectedCampaign) {
      alert("Please select a campaign.");
      return;
    }
    if (selectedCaller.length === 0) {
      alert("Please select at least one contact.");
      return;
    }
    const calls = selectedCaller
      .map((id) => {
        const entry = Object.values(callData).find(
          (data) =>
            (data.user.other?.Id || data.user._id || data.user.id) === id
        );
        return entry ? createCallObj(entry.user, id) : null;
      })
      .filter((call) => call !== null);
    if (calls.length === 0) {
      setError("No valid calls to initiate.");
      return;
    }
    console.log("Dynamic calls:", calls);
    await initiateCall(calls);
  };

  // Define table headers based on SheetData structure
  const tableHeaders = selectedCampaign?.sheetData?.[0]
    ? [
      "name",
      "phoneNumber",
      "callStatus",
      "email",
      ...Object.keys(selectedCampaign.sheetData[0].other || {}).filter(
        (key) => key !== "0"
      ),
    ]
    : [];

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Calculate assignments to display for current page
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentAssignments = assignments.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1); // reset when data changes
  }, [assignments.length, activeTab]);

  // Generate page numbers for pagination
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  return (
    <div className={styles.container}>
      <ToastContainer />
      <div className={styles.header}>
        <input
          type="text"
          placeholder="Search"
          className={styles.search}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div className={styles.dropdownContainer} ref={campaignDropdownRef}>
          <button
            className={styles.dropdown}
            onClick={() => {
              setShowCampaignDropdown(!showCampaignDropdown);
            }}
          >
            {selectedCampaign
              ? selectedCampaign.campaignname
              : "Select Campaign"}{" "}
            <FaAngleDown />
          </button>
          {showCampaignDropdown && (
            <div className={styles.selectTemplateContainer}>
              {campaigns.map((campaign) => (
                <p
                  key={campaign._id}
                  onClick={() => handleCampaignSelect(campaign)}
                >
                  {campaign.campaignname}
                </p>
              ))}
            </div>
          )}
        </div>

        {/* ADDED: ZeroSecondConnect Toggle Buttons - Only for Meta campaigns */}
        {/* {selectedCampaign && !!selectedCampaign.metaCampaignId && (
          <ZeroSecondConnectToggle
            campaign={selectedCampaign}
            onToggle={handleZeroSecondToggle}
          />
        )} */}

        {selectedCampaign && (
          selectedCampaign.metaCampaignId ||
          selectedCampaign.campaignType?.toLowerCase() === "web based"
        ) && (
            <ZeroSecondConnectToggle
              campaign={selectedCampaign}
              onToggle={handleZeroSecondToggle}
            />
          )}


        <button
          className={styles.button}
          onClick={handleDynamicCall}
          disabled={
            filteredData?.length === 0 ||
            !selectedCampaign ||
            selectedCaller.length === 0
          }
        >
          {selectedCaller.length === filteredData?.length &&
            filteredData?.length > 0
            ? "Call All"
            : `Call Selected (${selectedCaller.length})`}
          <MdOutlinePhone />
        </button>
      </div>
      {isLoading ? (
        <LoaderSpiner />
      ) : (
        <div className={styles.tableContainer}>
          {!isLoading && campaigns.length === 0 && !error && (
            <p>No campaigns available.</p>
          )}
          {!selectedCampaign && (
            <p className={styles.error}>
              Please select a campaign to view contacts.
            </p>
          )}
          {selectedCampaign && activeTab === "all" && (
            <>
              {filteredData?.length > 0 ? (
                <>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th className={styles.th}>
                          <input
                            type="checkbox"
                            checked={
                              filteredData?.length > 0 &&
                              selectedCaller.length === filteredData?.length
                            }
                            onChange={handleSelectAll}
                          />
                        </th>
                        {tableHeaders.map((key) => (
                          <th key={key} className={styles.th}>
                            {key.charAt(0).toUpperCase() + key.slice(1)}
                          </th>
                        ))}
                        <th className={styles.th}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentAssignments.map((item, index) => {
                        const id = item.other?.Id || item._id || index;
                        const callEntry = callData[id] || {
                          user: item,
                          callObj: {},
                        };
                        return (
                          <tr key={id}>
                            <td className={styles.td}>
                              <input
                                type="checkbox"
                                checked={selectedCaller.includes(id)}
                                onChange={() => handleCheckboxChange(id)}
                              />
                            </td>
                            {tableHeaders.map((key) => (
                              <td key={key} className={styles.td}>
                                {renderCellValue(
                                  key in item ? item[key] : item.other?.[key]
                                )}
                              </td>
                            ))}
                            <td className={styles.td}>
                              <span
                                className={styles.icon}
                                onClick={() =>
                                  handleIndividualCall({
                                    id: callEntry.callObj.id || id,
                                    user: item,
                                  })
                                }
                              >
                                <MdOutlinePhone />
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  <div className={styles.pagination}>
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={styles.pageButton}
                    >
                      Previous
                    </button>
                    {Array.from({ length: totalPages }, (_, index) => (
                      <button
                        key={index + 1}
                        onClick={() => handlePageChange(index + 1)}
                        className={`${styles.pageButton} ${currentPage === index + 1 ? styles.active : ""
                          }`}
                      >
                        {index + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={styles.pageButton}
                    >
                      Next
                    </button>
                  </div>
                </>
              ) : (
                <p>No contacts match your criteria.</p>
              )}
            </>
          )}
          {activeTab === "successful" && (
            <>
              {successfulCalls.length > 0 ? (
                <>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        {tableHeaders.map((key) => (
                          <th key={key} className={styles.th}>
                            {key.charAt(0).toUpperCase() + key.slice(1)}
                          </th>
                        ))}
                        <th className={styles.th}>Action</th>
                        <th className={styles.th}>Timestamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentAssignments.map((item) => (
                        <tr key={item.id}>
                          {tableHeaders.map((key) => (
                            <td key={key} className={styles.td}>
                              {renderCellValue(
                                key in item ? item[key] : item.other?.[key]
                              )}
                            </td>
                          ))}
                          <td className={styles.td}>
                            <span
                              className={styles.icon}
                              onClick={() => handleIndividualCall(item)}
                            >
                              <MdOutlinePhone />
                            </span>
                          </td>
                          <td className={styles.td}>
                            {item.timestamp
                              ? new Date(item.timestamp).toLocaleString()
                              : "N/A"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {totalPages > 1 && (
                    <div className={styles.pagination}>
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={styles.pageButton}
                      >
                        Previous
                      </button>
                      {Array.from({ length: totalPages }, (_, index) => (
                        <button
                          key={index + 1}
                          onClick={() => handlePageChange(index + 1)}
                          className={`${styles.pageButton} ${currentPage === index + 1 ? styles.active : ""
                            }`}
                        >
                          {index + 1}
                        </button>
                      ))}
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={styles.pageButton}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <p>No successful calls.</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CallBook;